import React from "react";
import { Link } from "react-router-dom";
import TechStackCitations from "./TechStackCitations";
import useSEO from "./useSEO";

const AboutPage = () => {
  useSEO({
    title: "About Core Spectators | Software, Shopify & SaaS Engineering Agency",
    description: "Discover Core Spectators: a specialized software development agency building high-converting Shopify 2.0 storefronts, Flutter mobile apps, SaaS MVPs, and autonomous AI workflow automations.",
    keywords: "about Core Spectators, software engineering agency, custom shopify store developers, flutter mobile app development team, saas mvp engineering agency, full stack software architects, enterprise ai automation consultants",
    canonical: "https://corespectators.com/About",
    schema: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Core Spectators Engineering Agency",
      "url": "https://corespectators.com/About",
      "description": "Core Spectators is a full-stack software and e-commerce engineering agency building bespoke Shopify 2.0 storefronts, cross-platform Flutter mobile applications, scalable SaaS MVPs, and autonomous AI workflows.",
      "mainEntity": {
        "@type": "Organization",
        "name": "Core Spectators",
        "url": "https://corespectators.com",
        "email": "contact@corespectators.com",
        "telephone": ["+1-332-378-8142", "+92-333-5136060"],
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+1-332-378-8142",
            "contactType": "US Client Desk",
            "availableLanguage": ["English"]
          },
          {
            "@type": "ContactPoint",
            "telephone": "+92-333-5136060",
            "contactType": "Global Engineering Desk & WhatsApp",
            "availableLanguage": ["English", "Urdu"]
          }
        ],
        "sameAs": [
          "https://www.linkedin.com/company/corespectators",
          "https://twitter.com/corespectators",
          "https://github.com/corespectators"
        ]
      }
    }
  });

  return (
    <div className="bg-white py-10">
      {/* First Section */}
      <div className="flex flex-col sm:flex-row items-center m-auto max-w-screen-xl px-4 py-8">
        <div className="sm:w-1/2 w-full ml-0 sm:ml-8 p-4 sm:p-5">
          <div className="text">
            <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
              About Us
            </span>
            <h1 className="my-2 font-extrabold text-3xl sm:text-4xl text-gray-900 leading-tight">
              Premier Software & Intelligent <span className="text-red-600">Engineering Agency</span>
            </h1>
            <div className="sm:hidden block p-4 py-5">
              <div className="image object-center text-center">
                <img src="/images/ab1.svg" alt="Core Spectators AI Engineering Team" className="w-full h-auto" />
              </div>
            </div>
            <p className="mb-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              Our mission is to empower businesses with bespoke high-converting Shopify 2.0 storefronts, silky 60fps Flutter mobile applications, scalable SaaS architectures, and autonomous AI workflow systems. We build production-ready digital products with zero technical compromises.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
              Since our inception in 2019, our engineering team has partnered with ambitious founders, established brands, and enterprise teams to turn architectural concepts into high-throughput digital platforms.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/Form"
                className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-6 rounded-lg shadow transition text-sm sm:text-base"
              >
                Start Project Discovery
              </Link>
              <Link
                to="/Portfolio"
                className="inline-block bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2.5 px-6 rounded-lg transition text-sm sm:text-base"
              >
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
        <div className="sm:w-1/2 hidden sm:flex justify-center p-6 sm:p-10">
          <div className="image object-center text-center">
            <img src="/images/ab1.svg" alt="Core Spectators AI Engineering Team" className="w-full max-h-[420px] object-contain drop-shadow" />
          </div>
        </div>
      </div>

      {/* Second Section */}
      <div className="flex flex-col-reverse sm:flex-row items-center m-auto max-w-screen-xl px-4 py-12 border-t border-gray-100">
        <div className="sm:w-1/2 hidden sm:flex justify-center p-6 sm:p-10">
          <div className="image object-center text-center">
            <img src="/images/ab2.svg" alt="Core Spectators AI Innovation" className="w-full max-h-[420px] object-contain drop-shadow" />
          </div>
        </div>
        <div className="sm:w-1/2 w-full ml-0 sm:ml-8 p-4 sm:p-5">
          <div className="text">
            <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
              Our Core Philosophy
            </span>
            <h2 className="my-2 font-extrabold text-3xl sm:text-4xl text-gray-900 leading-tight">
              Driven by <span className="text-red-600">Technical Rigor</span>, Performance & Direct Delivery
            </h2>
            <div className="sm:hidden block p-4 py-5">
              <div className="image object-center text-center">
                <img src="/images/ab2.svg" alt="Core Spectators AI Innovation" className="w-full h-auto" />
              </div>
            </div>
            <p className="mb-4 text-gray-600 text-sm sm:text-base leading-relaxed">
              At Core Spectators, we believe in engineering excellence over buzzwords. Every Shopify theme, mobile app, and backend API is architected with clean modular code, comprehensive automated testing, and zero vendor lock-in.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
              Our multidisciplinary team comprises full-stack React/Node engineers, Flutter mobile architects, Shopify Liquid specialists, and AI pipeline developers working collaboratively to deliver measurable enterprise value.
            </p>
            <Link
              to="/Contact"
              className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-6 rounded-lg shadow transition text-sm sm:text-base"
            >
              Contact Our Architects
            </Link>
          </div>
        </div>
      </div>

      {/* Verified Architectural Standards & Authoritative Tech Stack Citations */}
      <TechStackCitations />
    </div>
  );
};

export default AboutPage;